import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FaUsers, FaMapMarkerAlt, FaClipboardCheck, FaClock, FaSync, 
  FaUser, FaUserClock, FaFileAlt, FaTrophy, FaBell, FaDownload,
  FaArrowUp, FaArrowDown, FaChartLine
} from 'react-icons/fa';
import { Line, Bar } from 'react-chartjs-2';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { dashboardAPI, personnelAPI, checkinsAPI, reportsAPI, statisticsAPI } from '../../utils/api';
import '../../styles/modern-design-system.css';
import './AdminDashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const AdminDashboardModern = () => {
  const [stats, setStats] = useState({
    totalPersonnel: 0,
    activeSessions: 0,
    totalSites: 0,
    reportsToday: 0,
    sitesVisitedToday: 0,
    personnelList: [],
    activeSessionsList: [],
    reportsList: [],
    personnelChange: 0,
    sessionsChange: 0,
    sitesChange: 0,
    reportsChange: 0
  });
  
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityData, setActivityData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  
  // Monitor theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
        }
      });
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    
    return () => observer.disconnect();
  }, []);
  
  // Helper function to get chart colors based on theme
  const getChartColors = () => {
    if (isDarkMode) {
      return {
        textColor: '#F8FAFC',
        gridColor: 'rgba(148, 163, 184, 0.2)',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
      };
    }
    return {
      textColor: '#1E293B',
      gridColor: 'rgba(15, 23, 42, 0.1)',
      borderColor: 'rgba(15, 23, 42, 0.2)',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
    };
  };
  
  const chartColors = getChartColors();

  const fetchDashboardData = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const statsResponse = await dashboardAPI.getStats().catch(err => {
        console.error('Error fetching stats:', err.response || err);
        throw new Error(`Failed to load dashboard stats: ${err.message}`);
      });
      
      const [personnelResponse, activeSessionsResponse] = await Promise.all([
        personnelAPI.getAll({ status: 'active' }),
        checkinsAPI.getAllActive().catch(err => {
          console.error('Error fetching active sessions:', err);
          return { data: [] };
        })
      ]);

      const today = new Date();
      const startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);

      // Fetch recent activity (for display) - fetch all recent activity
      let recentActivityData = [];
      try {
        const activityResponse = await dashboardAPI.getActivity();
        recentActivityData = activityResponse?.data || [];
        setRecentActivity(recentActivityData);
      } catch (error) {
        console.error('Error fetching activity:', error);
        setRecentActivity([]);
      }

      // Get active sessions from recent activity
      let activeSessionsData = [];
      if (recentActivityData && recentActivityData.length > 0) {
        const activePersonnelMap = new Map();
        
        recentActivityData.forEach(activity => {
          if (activity.type === 'check-in' && !activity.checkedOut) {
            if (activity.userId && activity.userName) {
              activePersonnelMap.set(activity.userId, {
                id: activity.userId,
                name: activity.userName
              });
            }
          }
        });
        
        activeSessionsData = Array.from(activePersonnelMap.values());
      }
      
      // Calculate unique sites visited TODAY by fetching filtered activity from API
      const uniqueSitesVisited = new Set();
      try {
        const todayActivityResponse = await statisticsAPI.getActivity({
          startDate: startOfDay.toISOString(),
          endDate: endOfDay.toISOString()
        });
        
        const todayActivityData = todayActivityResponse?.data || [];
        if (Array.isArray(todayActivityData)) {
          todayActivityData.forEach(activity => {
            // Look for site information in various possible fields
            if (activity.siteId) {
              uniqueSitesVisited.add(activity.siteId);
            } else if (activity.site?.id) {
              uniqueSitesVisited.add(activity.site.id);
            } else if (activity.checkIn?.siteId) {
              uniqueSitesVisited.add(activity.checkIn.siteId);
            } else if (activity.checkIn?.site?.id) {
              uniqueSitesVisited.add(activity.checkIn.site.id);
            }
          });
        }
      } catch (err) {
        console.error('Error fetching today\'s activity for sites visited:', err);
      }
      
      const sitesVisitedToday = uniqueSitesVisited.size;
      
      const reportsResponse = await reportsAPI.getAllReports({
        startDate: startOfDay.toISOString(),
        endDate: endOfDay.toISOString(),
        sort: '-createdAt'
      }).catch(err => {
        console.error('Error fetching today\'s reports:', err);
        return { data: [] };
      });

      let reportsData = Array.isArray(reportsResponse?.data) ? reportsResponse.data : [];
      reportsData = reportsData.filter(report => {
        const reportDate = new Date(report.createdAt || report.date || report.submittedAt);
        return reportDate >= startOfDay && reportDate <= endOfDay;
      });
      const reportsToday = reportsData.length;
      
      const personnelData = Array.isArray(personnelResponse?.data) ? personnelResponse.data : [];
      const totalPersonnel = personnelData.length;
      
      const personnelList = personnelData.slice(0, 10).map(p => ({ id: p.id, name: p.name }));
      const reportsList = reportsData.slice(0, 10).map(report => ({
        id: report.id,
        title: report.title || 'Untitled Report',
        personnelName: report.personnelName || report.user?.name || 'Unknown User',
        userId: report.userId || report.user?.id,
        date: report.createdAt || report.date
      }));
      
      const finalActiveSessionsList = activeSessionsData.length > 0 
        ? activeSessionsData 
        : Array.isArray(activeSessionsResponse?.data?.data)
          ? activeSessionsResponse.data.data.map(session => ({
              id: session.user?.id || session.id,
              name: session.user?.name || 'Unknown User'
            }))
          : [];
      
      setStats({
        ...statsResponse?.data,
        totalPersonnel,
        reportsToday,
        sitesVisitedToday,
        personnelList,
        activeSessionsList: finalActiveSessionsList,
        activeSessions: finalActiveSessionsList.length,
        reportsList,
        personnelChange: 5,
        sessionsChange: -12,
        sitesChange: 2,
        reportsChange: 18
      });
      
      // Fetch weekly activity data
      try {
        const weeklyResponse = await dashboardAPI.getWeeklyActivity();
        setActivityData(weeklyResponse?.data || { labels: [], datasets: [] });
      } catch (error) {
        console.error('Error fetching weekly activity:', error);
        setActivityData({ labels: [], datasets: [] });
      }
      
      setLastRefreshed(new Date());
      setError(null);
    } catch (error) {
      console.error("Dashboard error:", error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load dashboard data. Please try again later.';
      setError(errorMessage);
      
      if (error.response?.status === 401) {
        console.log('Authentication required. Redirecting to login...');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (!autoRefreshEnabled) return;
    
    const interval = setInterval(() => {
      fetchDashboardData();
    }, refreshInterval * 1000);
    
    return () => clearInterval(interval);
  }, [refreshInterval, autoRefreshEnabled]);

  // Enhanced chart configuration
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: chartColors.textColor,
          font: {
            family: 'var(--font-family)',
            size: 12,
            weight: 600
          },
          usePointStyle: true,
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: chartColors.backgroundColor,
        titleColor: chartColors.textColor,
        bodyColor: chartColors.textColor,
        borderColor: chartColors.borderColor,
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: chartColors.gridColor,
          drawBorder: false
        },
        ticks: {
          color: chartColors.textColor,
          font: {
            family: 'var(--font-family)',
            size: 11
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: chartColors.textColor,
          font: {
            family: 'var(--font-family)',
            size: 11
          }
        }
      }
    }
  };

  return (
    <div className="admin-dashboard-modern">
      <Container fluid className="px-3 px-md-4 px-lg-5" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        {/* Header with Actions */}
        <Row className="mb-4 animate-fade-in">
          <Col xs={12} lg={8}>
            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: 800,
              marginBottom: '0.5rem',
              background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-blue-light))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Admin Dashboard
            </h1>
            <p style={{
              fontSize: 'var(--font-size-lg)',
              color: 'var(--text-muted)',
              fontWeight: 500
            }}>
              Real-time field operations monitoring
            </p>
          </Col>
          <Col xs={12} lg={4} className="d-flex align-items-center justify-content-start justify-content-lg-end gap-2 mt-3 mt-lg-0 flex-wrap">
            <div style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              background: 'var(--surface-glass)',
              backdropFilter: 'blur(8px)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-full)',
              fontWeight: 500
            }}>
              <FaClock size={12} style={{ color: 'var(--accent-blue)' }} />
              <span>Updated {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <button 
              className="btn-primary-modern"
              onClick={handleRefresh}
              disabled={refreshing}
              style={{ 
                minWidth: '120px',
                padding: '0.625rem 1.25rem',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {refreshing ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <FaSync />
                  <span>Refresh</span>
                </>
              )}
            </button>
          </Col>
        </Row>

        {/* Auto-refresh Controls */}
        <div className="glass-card animate-slide-in" style={{ padding: 'var(--spacing-lg)', marginBottom: '2rem' }}>
          <Row className="align-items-center g-3">
            <Col xs={12} md="auto">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <label style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  marginBottom: 0
                }}>
                  Auto-refresh:
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: 0 }}>
                  <input 
                    type="checkbox" 
                    checked={autoRefreshEnabled}
                    onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: 'var(--accent-blue)'
                    }}
                  />
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
                    Enabled
                  </span>
                </label>
              </div>
            </Col>
            <Col xs={12} md="auto">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-secondary)',
                  marginBottom: 0
                }}>
                  Every:
                </label>
                <select 
                  className="form-select form-select-sm"
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(Number(e.target.value))}
                  disabled={!autoRefreshEnabled}
                  style={{
                    width: 'auto',
                    background: 'var(--surface-glass)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    padding: '0.375rem 2rem 0.375rem 0.75rem'
                  }}
                >
                  <option value="10">10 seconds</option>
                  <option value="30">30 seconds</option>
                  <option value="60">1 minute</option>
                  <option value="300">5 minutes</option>
                </select>
              </div>
            </Col>
          </Row>
        </div>

        {error && (
          <Alert variant="danger" className="animate-slide-in" style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid var(--error-red)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: '2rem'
          }}>
            <Alert.Heading>Error Loading Dashboard Data</Alert.Heading>
            <p>{error}</p>
          </Alert>
        )}

        {/* KPI Cards Grid */}
        <Row className="g-3 g-md-4 mb-4 animate-stagger">
          {/* Total Personnel Card */}
          <Col xs={12} sm={6} lg={3}>
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 100, hide: 10000 }}
              overlay={
                <Tooltip id="personnel-tooltip" className="custom-tooltip">
                  <div style={{ padding: '0.5rem' }}>
                    <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
                      <FaUser style={{ marginRight: '0.5rem' }} />
                      All Personnel ({stats.totalPersonnel})
                    </div>
                    {stats.personnelList.length > 0 ? (
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '200px', overflowY: 'auto' }}>
                        {stats.personnelList.map((person) => (
                          <li key={person.id} style={{ padding: '0.25rem 0' }}>
                            {person.name}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div style={{ fontSize: '0.875rem', opacity: 0.7 }}>No personnel found</div>
                    )}
                  </div>
                </Tooltip>
              }
            >
              <div className="kpi-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="kpi-icon kpi-icon-blue">
                    <FaUsers />
                  </div>
                </div>
                <div>
                  <div className="kpi-label">Total Personnel</div>
                  <div className="kpi-metric">
                    {loading ? <Spinner animation="border" size="sm" /> : stats.totalPersonnel}
                  </div>
                </div>
                <Link to="/admin/personnel" style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--accent-blue)',
                  textDecoration: 'none',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  marginTop: '0.5rem'
                }}>
                  <span>View all</span>
                  <FaChartLine size={12} />
                </Link>
              </div>
            </OverlayTrigger>
          </Col>

          {/* Active Sessions Card */}
          <Col xs={12} sm={6} lg={3}>
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 100, hide: 10000 }}
              overlay={
                <Tooltip id="sessions-tooltip" className="custom-tooltip">
                  <div style={{ padding: '0.5rem' }}>
                    <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
                      <FaUserClock style={{ marginRight: '0.5rem' }} />
                      Active Personnel ({stats.activeSessions || 0})
                    </div>
                    {stats.activeSessions > 0 && Array.isArray(stats.activeSessionsList) ? (
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '200px', overflowY: 'auto' }}>
                        {stats.activeSessionsList.map((session) => (
                          <li key={session.id} style={{ padding: '0.25rem 0' }}>
                            {session.name || 'Unknown User'}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div style={{ fontSize: '0.875rem', opacity: 0.7 }}>No active sessions</div>
                    )}
                  </div>
                </Tooltip>
              }
            >
              <div className="kpi-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="kpi-icon kpi-icon-success">
                    <FaClock />
                  </div>
                </div>
                <div>
                  <div className="kpi-label">Active Sessions</div>
                  <div className="kpi-metric" style={{
                    background: 'linear-gradient(135deg, var(--success-emerald), var(--success-light))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    {loading ? <Spinner animation="border" size="sm" /> : stats.activeSessions}
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    marginTop: '0.5rem',
                    fontWeight: 500
                  }}>
                    {loading ? '...' : `${stats.totalPersonnel > 0 ? Math.round((stats.activeSessions / stats.totalPersonnel) * 100) : 0}% of total personnel`}
                  </div>
                </div>
                <Link to="/admin/statistics" style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--success-emerald)',
                  textDecoration: 'none',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  marginTop: '0.5rem'
                }}>
                  <span>View active</span>
                  <FaChartLine size={12} />
                </Link>
              </div>
            </OverlayTrigger>
          </Col>

          {/* Total Sites Card */}
          <Col xs={12} sm={6} lg={3}>
            <div className="kpi-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div className="kpi-icon kpi-icon-blue" style={{
                  background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(59, 130, 246, 0.1))'
                }}>
                  <FaMapMarkerAlt />
                </div>
              </div>
              <div>
                <div className="kpi-label">Total Sites</div>
                <div className="kpi-metric" style={{
                  background: 'linear-gradient(135deg, var(--accent-blue-light), var(--accent-blue))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {loading ? <Spinner animation="border" size="sm" /> : stats.totalSites}
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)',
                  marginTop: '0.5rem',
                  fontWeight: 500
                }}>
                  Visited Sites: {loading ? '...' : `${stats.sitesVisitedToday} / ${stats.totalSites}`}
                </div>
              </div>
              <Link to="/admin/sites" style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--accent-blue-light)',
                textDecoration: 'none',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                marginTop: '0.5rem'
              }}>
                <span>Manage sites</span>
                <FaChartLine size={12} />
              </Link>
            </div>
          </Col>

          {/* Reports Today Card */}
          <Col xs={12} sm={6} lg={3}>
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 100, hide: 10000 }}
              overlay={
                <Tooltip id="reports-tooltip" className="custom-tooltip">
                  <div style={{ padding: '0.5rem' }}>
                    <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
                      <FaFileAlt style={{ marginRight: '0.5rem' }} />
                      Today's Reports ({stats.reportsList.length})
                    </div>
                    {stats.reportsList.length > 0 ? (
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '200px', overflowY: 'auto' }}>
                        {stats.reportsList.map((report) => (
                          <li key={report.id} style={{ padding: '0.25rem 0' }}>
                            <Link 
                              to={`/admin/reports/${report.id}`}
                              style={{ color: 'inherit', textDecoration: 'none' }}
                            >
                              {report.personnelName || `Report #${report.id}`}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div style={{ fontSize: '0.875rem', opacity: 0.7 }}>No reports today</div>
                    )}
                  </div>
                </Tooltip>
              }
            >
              <div className="kpi-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="kpi-icon kpi-icon-warning">
                    <FaClipboardCheck />
                  </div>
                </div>
                <div>
                  <div className="kpi-label">Reports Today</div>
                  <div className="kpi-metric" style={{
                    background: 'linear-gradient(135deg, var(--warning-amber), var(--warning-light))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    {loading ? <Spinner animation="border" size="sm" /> : stats.reportsToday}
                  </div>
                </div>
                <Link to="/admin/statistics" style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--warning-amber)',
                  textDecoration: 'none',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  marginTop: '0.5rem'
                }}>
                  <span>View all</span>
                  <FaChartLine size={12} />
                </Link>
              </div>
            </OverlayTrigger>
          </Col>
        </Row>

        {/* Weekly Activity Chart */}
        <div className="glass-card animate-fade-in" style={{ marginBottom: '2rem' }}>
          <div style={{
            padding: 'var(--spacing-xl)',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <h3 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
                marginBottom: '0.25rem',
                color: 'var(--text-primary)'
              }}>
                Weekly Activity
              </h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: 0 }}>
                Check-ins, check-outs, and reports overview
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {refreshing && <Spinner animation="border" size="sm" style={{ color: 'var(--accent-blue)' }} />}
              <button 
                className="btn-outline-modern" 
                style={{ 
                  fontSize: 'var(--font-size-sm)',
                  padding: '0.625rem 1rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all var(--transition-base)'
                }}
                onClick={() => {
                  // Add export functionality here
                  console.log('Exporting data...');
                }}
              >
                <FaDownload size={14} />
                <span className="d-none d-md-inline">Export CSV</span>
                <span className="d-inline d-md-none">Export</span>
              </button>
            </div>
          </div>
          <div style={{ padding: 'var(--spacing-xl)', minHeight: '300px' }}>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <Spinner animation="border" style={{ color: 'var(--accent-blue)' }} />
              </div>
            ) : activityData.labels ? (
              <div style={{ height: '300px' }}>
                <Bar data={activityData} options={chartOptions} />
              </div>
            ) : (
              <div className="text-center text-muted py-5">No activity data available</div>
            )}
          </div>
        </div>

        {/* Recent Activity Table */}
        <div className="glass-card animate-fade-in">
          <div style={{
            padding: 'var(--spacing-xl)',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <h3 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
                marginBottom: '0.25rem',
                color: 'var(--text-primary)'
              }}>
                Recent Activity
              </h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: 0 }}>
                Live feed of field operations • {recentActivity.length} recent activities
              </p>
            </div>
            {refreshing && <Spinner animation="border" size="sm" style={{ color: 'var(--accent-blue)' }} />}
          </div>

          <div style={{ padding: 'var(--spacing-lg)' }}>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: 'var(--accent-blue)' }} />
              </div>
            ) : recentActivity.length > 0 ? (
              <div className="table-responsive">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Personnel</th>
                      <th>Action</th>
                      <th className="d-none d-md-table-cell">Site</th>
                      <th className="d-none d-lg-table-cell">Location</th>
                      <th className="d-none d-md-table-cell">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.map(activity => (
                      <tr key={activity.id}>
                        <td>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                              {activity.personnel || 'Unknown'}
                            </div>
                            <div className="d-md-none" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                              {activity.site || '—'}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge ${
                            activity.action === 'Checked in' ? 'status-badge-success' :
                            activity.action === 'Checked out' ? 'status-badge-error' : 'status-badge-warning'
                          }`}>
                            {activity.action}
                          </span>
                        </td>
                        <td className="d-none d-md-table-cell" style={{ color: 'var(--text-secondary)' }}>
                          {activity.site || '—'}
                        </td>
                        <td className="d-none d-lg-table-cell" style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                          {activity.siteAddress || 
                          activity.address ||
                          activity.locationName ||
                          activity.location ||
                          (activity.latitude && activity.longitude
                            ? `${activity.latitude.toFixed(4)}, ${activity.longitude.toFixed(4)}`
                            : 'Unknown location')}
                        </td>
                        <td className="d-none d-md-table-cell" style={{ color: 'var(--text-secondary)' }}>
                          {activity.time ? 
                            new Date(activity.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 
                            '—'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-muted py-5">No recent activity</div>
            )}
            
            <div style={{ marginTop: 'var(--spacing-lg)', textAlign: 'center' }}>
              <Link 
                to="/admin/statistics" 
                className="btn-primary-modern" 
                style={{ 
                  textDecoration: 'none',
                  padding: '0.75rem 2rem',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaChartLine size={16} />
                <span>View All Activity</span>
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default AdminDashboardModern;
